import { Box, Popover, Typography } from "@mui/material";

import { skillAccessCaption } from "../../SkillAccess/skillAccessTokens";
import { type ScoutSkillMenuItem } from "./skillMenuModel";
import { SkillOwnerIcon } from "./SkillOwnerIcon";

type ScoutSkillDetailPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Always known: the slug the agent activated or the user typed. */
  slug: string;
  /** Catalog entry. Undefined when the user can't see the skill, or it hasn't loaded. */
  skill?: ScoutSkillMenuItem;
  /** Name to show when `skill` is undefined (e.g. from the tool result). */
  fallbackName?: string;
  /** Lets the trigger point `aria-controls` at the popover. */
  id?: string;
};

/**
 * Single-skill detail view: title, `/slug`, scoping (owner and audience) and
 * the full description. Anchored below its trigger. Click and key events are
 * stopped here because React events bubble through the portal, and a trigger
 * inside a clickable row (the tool-group header) must not also toggle it.
 */
export function ScoutSkillDetailPopover({
  open,
  anchorEl,
  onClose,
  slug,
  skill,
  fallbackName,
  id,
}: ScoutSkillDetailPopoverProps) {
  const title = skill?.displayName ?? fallbackName ?? `/${slug}`;
  const stop = (event: { stopPropagation: () => void }) => event.stopPropagation();

  return (
    <Popover
      id={id}
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      onClick={stop}
      onKeyDown={stop}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-label": `${title} skill`,
          sx: { mt: 0.75, width: 420, maxWidth: "calc(100vw - 32px)", borderRadius: 2 },
        },
      }}
    >
      <Box sx={{ px: 2, pt: 1.75, pb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", columnGap: 1, flexWrap: "wrap" }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="primary" data-testid="skill-detail-slug">
            /{slug}
          </Typography>
        </Box>
        {skill ? (
          <>
            <Box
              data-testid="skill-detail-scope"
              sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary" }}
            >
              <SkillOwnerIcon owner={skill.owner} size={14} />
              <Typography variant="caption">{skillAccessCaption(skill)}</Typography>
            </Box>
            <Typography variant="body2" sx={{ lineHeight: 1.5, whiteSpace: "pre-line" }}>
              {skill.description}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Details for this skill aren't available to you.
          </Typography>
        )}
      </Box>
    </Popover>
  );
}
